import { Alert, Typography } from '@mui/material'
import React from 'react'

import ExternalLink from '@/app/component/atom/external-link'
import { Form } from '@/app/component/form'
import { ActionsContainer, FlexFormContainer } from '@/app/component/form/common'
import SubmitButton from '@/app/component/form/submit-button'
import { useInviteUser } from '@/app/module/user/hooks/use-invite-user'

import InviteUserConfirmationDialog from './invite-user-confirmation-modal'
import { UserInviteFormValues, userInviteDefaultValues, userInviteFields, userInviteSchema } from './specs'

type Props = {
  onSuccess: () => void
}

const InviteUserForm: React.FC<Props> = ({ onSuccess }) => {
  const [inviteUser, { isLoading }] = useInviteUser()
  const [pendingFormData, setPendingFormData] = React.useState<UserInviteFormValues | undefined>(undefined)

  const handleSubmit = React.useCallback(async (data: UserInviteFormValues) => {
    setPendingFormData(data)
  }, [])

  const handleConfirmationClose = React.useCallback(() => {
    setPendingFormData(undefined)
  }, [])

  const handleConfirmationConfirm = React.useCallback(async () => {
    if (!pendingFormData) {
      return
    }

    const success = await inviteUser(pendingFormData)
    if (success) {
      onSuccess()
    }
  }, [pendingFormData, inviteUser, onSuccess])

  return (
    <FlexFormContainer>
      <Typography variant="caption" color="textSecondary">
        Invited users will receive an invitation email to this organization. Upon acceptance of the invitation, invited
        users will join with the selected role.
      </Typography>
      <Form
        defaultValues={userInviteDefaultValues}
        fields={userInviteFields}
        schema={userInviteSchema}
        onSubmit={handleSubmit}
        renderActions={({ isDirty }) => (
          <ActionsContainer>
            <SubmitButton isDirty={isDirty} isSubmitting={isLoading}>
              Invite
            </SubmitButton>
          </ActionsContainer>
        )}
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          To give the user almost complete control, choose Manager.
          <br />
          For a little less control, Collaborator. For CATI call access only, CATI Agent. Read more about user roles{' '}
          <ExternalLink href="https://www.engagespark.com/support/add-users-and-manage-what-they-can-access/">
            here
          </ExternalLink>
        </Alert>
      </Form>
      {pendingFormData && (
        <InviteUserConfirmationDialog
          formData={pendingFormData}
          onClose={handleConfirmationClose}
          onConfirm={handleConfirmationConfirm}
        />
      )}
    </FlexFormContainer>
  )
}

export default InviteUserForm
